import { isRouteErrorResponse, Link, useNavigate, useRouteError } from 'react-router';
import { isApiError } from '@sl/core';
import { isLiveDatasetError, NoLiveDatasetScreen } from './api/liveLook';
import { GENERIC_ERROR_TEXT, messageText } from './messages';
import { StaleBuildScreen } from './StaleBuildScreen';
import { isStaleBuildError } from './staleBuild';
import { testId } from './testId';
import styles from './RouteErrorBoundary.module.css';

// Per-route error boundary (ARCH §9.1): a render error or a thrown loader error on ONE route shows this in
// that route's place; the rest of the app (and the aria-live region) stays up. Users read plain English
// (`ApiError.userMessage`, never `error.message`, REQ-287 AC3); the developer detail goes to the console.

export function describeRouteError(error: unknown): { title: string; text: string } {
  if (isApiError(error)) {
    return { title: "This page couldn't load", text: messageText(error.userMessage) };
  }
  if (isRouteErrorResponse(error) && error.status === 404) {
    return {
      title: "There's nothing at this address",
      text: 'The link may be old. Your ads and spend are not affected.',
    };
  }
  return { title: 'Something went wrong on this page', text: GENERIC_ERROR_TEXT };
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  if (import.meta.env.DEV) console.error('[route error]', error);

  // The stale build is its own screen, its own id and its own single action (BUG-054). The same component
  // is what `App` shows when the guard hears a failure the router never surfaced as a route error.
  if (isStaleBuildError(error)) return <StaleBuildScreen />;
  // T-283: `?dataset=live` with nothing pulled beside the app. Not an error of the page's — the file a reader
  // has not made yet is not missing, it is unmade — so it gets the state that says which command makes it
  // rather than "Something went wrong on this page". `App` shows the same screen for the whole app.
  if (isLiveDatasetError(error)) return <NoLiveDatasetScreen />;

  const { title, text } = describeRouteError(error);
  return (
    <main className={styles.wrap} data-testid={testId('state', 'error')}>
      <section className={styles.card} role="alert" aria-labelledby="route-error-title">
        <h1 id="route-error-title" className={styles.title}>
          {title}
        </h1>
        <p className={styles.text}>{text}</p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primary}
            onClick={() => void navigate(0)}
            data-testid={testId('state', 'error', 'retry')}
          >
            Try again
          </button>
          <Link to="/" className={styles.secondary} data-testid={testId('state', 'error', 'home')}>
            Back to the start
          </Link>
        </div>
      </section>
    </main>
  );
}
